import { USER_ROLES } from '@hygge/shared';
import { isSuperAdminOnlyCode } from '@/features/settings/lib/permission-matrix';

/**
 * The system roles the Add Employee / Hire form may offer. Only a super_admin
 * may give an account the super_admin role — the server answers 403 to anyone
 * else — so the option is not listed for them. Kept out of the component for
 * the node vitest (see profile-tabs.ts). [HW-1061]
 */
export function assignableRoles<T extends { name: string }>(
  roles: T[],
  isSuperAdmin: boolean
): T[] {
  // Trimmed, as the Permission Matrix and the server compare it. [HW-1062]
  return isSuperAdmin ? roles : roles.filter((role) => role.name.trim() !== USER_ROLES.SUPER_ADMIN);
}

/**
 * Whether only a super_admin may give an account this role: it holds a code
 * only super_admin may grant (editing the matrix or the catalog, Login as
 * employee, the placeholder) or a wildcard covering one. The server answers 403 to anyone else,
 * so the form shows such a role disabled, with the reason. [HW-1062]
 */
export function isSuperAdminOnlyRole(role: { permissions?: { code: string }[] }): boolean {
  return (role.permissions ?? []).some((p) => isSuperAdminOnlyCode(p.code));
}

/**
 * The role the System role field starts on when nothing is suggested: the
 * `employee` role, unless it is locked for this user — then none, so the
 * field shows "No account" instead of a choice the server refuses. [HW-1062]
 */
export function pickDefaultRole<T extends { id: string; name: string }>(
  roles: T[],
  lockedRoleIds: Set<string>
): T | undefined {
  const employee = roles.find((r) => r.name.trim() === 'employee');
  return employee && !lockedRoleIds.has(employee.id) ? employee : undefined;
}
